import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { prefetchAuthApi } from '@/hooks/UseApiCalls';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';
import { DASHBOARD_CORE_STALE_MS, dashboardCoreUrls } from './useDashboardData';

/**
 * SPR-10303: הקרן נפתחת על "תמונת מצב", והנתונים שלה יוצאים לדרך בלי לחכות לתור.
 *
 * בלי זה הפתיחה היא שרשרת: רשימת הקרנות, אחריה הדגלים של הקרן, אחריהם עליית המסך,
 * ורק אז התנועות - הקריאה הכבדה שהגרפים מחכים לה. כאן התנועות והיתרות בפתיחה
 * ובסגירה נשלחות ברגע שמזהה הקרן ידוע, במקביל לשתי הקריאות האלה, והמסך מצטרף
 * לבקשה שכבר בדרך או קורא את התשובה.
 *
 * רק שתי הקריאות שאינן תלויות בדגלים של הקרן או בעולמות ההרשאה (ר' coreUrls). השאר
 * קלות יותר ויוצאות כשהמסך עולה.
 *
 * `enabled` נקבע בצד הקורא: רק כשהכתובת מובילה לתמונת מצב והדגל פתוח למשתמש, כדי
 * שרכז שנוחת על לוח התנועות לא ישלם בקריאה שאיש לא קורא.
 */
const useDashboardPrefetch = (fundId: number | undefined, enabled: boolean) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const token = currentUser?.token;

  useEffect(() => {
    // -1 הוא "כל הפרויקטים" של מנהל, והשרת דוחה בו את היתרות בפתיחה ובסגירה.
    if (!enabled || !token || !fundId || fundId <= 0) return;
    const options = { staleTime: DASHBOARD_CORE_STALE_MS, meta: { refreshable: true } satisfies RefreshableQueryMeta };
    const { txUrl, balanceRangeUrl } = dashboardCoreUrls(fundId);
    void prefetchAuthApi(queryClient, txUrl, token, options);
    void prefetchAuthApi(queryClient, balanceRangeUrl, token, options);
  }, [enabled, fundId, token, queryClient]);
};

export default useDashboardPrefetch;
